/**
 * MindCore VibeSDK Web Dashboard
 * Main application component
 */

import React, { useState, useEffect } from 'react';
import { SwarmClient } from '@mindcore/sdk';
import { CreateSwarmForm } from './components/CreateSwarmForm';
import { SwarmDashboard } from './components/SwarmDashboard';
import { SwarmList } from './components/SwarmList';


const API_URL = import.meta.env.VITE_API_URL || 'https://mindcore-api.singlefabric.workers.dev';

const App: React.FC = () => {
  const [client] = useState(() => new SwarmClient({ apiUrl: API_URL }));
  const [activeView, setActiveView] = useState<'list' | 'create' | 'dashboard'>('list');
  const [activeSwarmId, setActiveSwarmId] = useState<string | null>(null);
  const [recentSwarms, setRecentSwarms] = useState<string[]>([]);

  // Load recent swarms from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('mindcore_recent_swarms');
    if (saved) {
      try {
        setRecentSwarms(JSON.parse(saved));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  // Save recent swarms to localStorage
  useEffect(() => {
    localStorage.setItem('mindcore_recent_swarms', JSON.stringify(recentSwarms));
  }, [recentSwarms]);

  const handleSwarmCreated = (swarmId: string) => {
    setRecentSwarms(prev => [swarmId, ...prev.slice(0, 9)]);
    setActiveSwarmId(swarmId);
    setActiveView('dashboard');
  };

  const handleViewSwarm = (swarmId: string) => {
    setActiveSwarmId(swarmId);
    setActiveView('dashboard');
  };

  const handleBackToList = () => {
    setActiveSwarmId(null);
    setActiveView('list');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                MindCore
              </span>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">VibeSDK</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                API: {API_URL}
              </span>
              <a
                href="/docs"
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Docs
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      {activeView !== 'dashboard' && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveView('list')}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeView === 'list'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Swarms
              </button>
              <button
                onClick={() => setActiveView('create')}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeView === 'create'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Create New
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'list' && (
          <SwarmList
            client={client}
            recentSwarms={recentSwarms}
            onViewSwarm={handleViewSwarm}
            onCreateNew={() => setActiveView('create')}
          />
        )}

        {activeView === 'create' && (
          <CreateSwarmForm
            client={client}
            onCreated={handleSwarmCreated}
            onCancel={() => setActiveView('list')}
          />
        )}

        {activeView === 'dashboard' && activeSwarmId && (
          <SwarmDashboard
            client={client}
            swarmId={activeSwarmId}
            onClose={handleBackToList}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
              MindCore Multi-Agent Swarm Platform
            </div>
            <div>
              Powered by Cloudflare Workers, Durable Objects & AI Gateway
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
